<template>
  <div
    :class="isSidebarOpen ? 'bg-background-secondary' : ''"
    class="px-2.5 py-2.5 relative rounded-full flex h-10 w-10 items-center justify-center cursor-pointer hover:bg-background-secondary transition-all text-[#344054] shrink-0"
    @click="openSidebar"
  >
    <Transition appear mode="out-in" name="fade">
      <div v-if="runningTaskLength && !isSidebarOpen">
        <!-- Background gray circle -->
        <div
          class="absolute inset-0 border-2 border-gray-300 rounded-full"
        ></div>
        <!-- Spinning top border arc -->
        <div
          class="absolute inset-0 border-2 border-t-black border-l-transparent border-b-transparent border-r-transparent rounded-full animate-spin"
        ></div>
        <span>
          {{ runningTaskLength }}
        </span>
      </div>
      <Icon v-else height="20" icon="solar:album-bold" />
    </Transition>
  </div>

  <!-- Sidebar (slides over the overlay) -->
  <aside
    class="z-50 h-[calc(100vh-60.5px)] fixed top-[60.5px] right-0 md:border-l overflow-y-auto bg-[#F7F9FB] text-left py-5 px-4 transform transition-transform ease-linear duration-300 w-60 shadow-genaration-panel"
    :class="isSidebarOpen ? 'translate-x-0' : 'translate-x-full'"
  >
    <div>
      <div class="flex gap-x-2 items-center text-label-md">
        <div
          class="border border-border-primary p-2 bg-white rounded-md cursor-pointer"
          @click="isSidebarOpen = false"
        >
          <Icon width="20" icon="ri:close-line"></Icon>
        </div>
        <span v-show="isSidebarOpen" class="text-label-md">GENERATIONS</span>
      </div>
      <div class="mt-3">
        <template v-if="isSidebarOpen">
          <div v-if="isSkeletonLoading" class="h-28 mb-2">
            <Skeleton class="h-28 w-full bg-muted-foreground/20" />
          </div>
          <div v-if="SidebarPanelData?.length" class="flex flex-col gap-2">
            <div
              v-for="(item, index) in SidebarPanelData"
              :key="index"
              class="flex gap-x-2 py-2 px-3 rounded-lg border justify-between cursor-pointer bg-white"
              :class="{
                'pointer-events-none':
                  item.type == 'aiVideo' && !item?.downloadUrl,
              }"
              @click="handleThumbClick(item)"
            >
              <div class="relative">
                <AdvanceImage
                  class="rounded overflow-hidden flex flex-shrink-0 w-[76px] object-cover h-24"
                  img-class="h-full object-cover"
                  :src="item?.thumbnail"
                  alt="generated Images"
                />
                <Icon
                  v-if="
                    item.type != 'aiPhotoshoot' &&
                    item?.status != 'Success' &&
                    item?.status != 'Fail'
                  "
                  width="16px"
                  class="right-1.5 bottom-1.5 absolute text-white"
                  icon="svg-spinners:ring-resize"
                />
                <div
                  v-if="
                    item.type != 'aiPhotoshoot' &&
                    item?.status != 'Success' &&
                    item?.status != 'Fail'
                  "
                  class="absolute w-full h-full justify-center items-center flex top-0 right-0 rounded"
                >
                  <div
                    class="bg-black/30 animate-pulse w-full h-full rounded"
                  ></div>
                </div>
              </div>
              <div class="flex flex-col gap-y-1 w-full">
                <div
                  class="px-1 py-0.5 rounded bg-background-information w-max"
                >
                  <span class="text-text-info font-medium text-[10px] block">
                    {{ item?.images ? "AI Photoshoot" : "AI Video" }}
                  </span>
                </div>
                <div
                  v-if="item?.prompt?.length"
                  class="border border-dashed rounded p-1 text-xs text-text-secondary overflow-hidden text-ellipsis bg-background-secondary text-left"
                >
                  {{
                    item?.prompt?.length > 40
                      ? item?.prompt?.slice(0, 40 - 3) + "..."
                      : item?.prompt
                  }}
                </div>
              </div>
            </div>
          </div>
          <div v-else class="flex flex-col gap-y-1">
            <img :src="videoMockup" alt="plaheholder thumb" />
            <span class="text-sm text-text-secondary text-center">
              No generations yet
            </span>
          </div>
        </template>
      </div>
    </div>
  </aside>
  <PhotoshootModal
    :selected-modal-image="aiPhotoshootData"
    :selected-index="selectedIndex"
  />
  <VideoGenerationModal :selected-modal-data="selectedVideoModalData" />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, markRaw, watch, onUnmounted } from "vue";
import videoMockup from "../../assets/videoMockup.jpg";
import { useSubscription } from "@vueuse/rxjs";
import {
  invokeDownloadStatusAPI,
  openGenerationSidebar,
  closeGenerationSidebar,
  openPhotoshootModal,
  openAIVideoModal,
  startSkeletonLoader,
  stopSkeletonLoader,
} from "@/lib/utils.js";
import FirebaseServices from "@/services/firebase.js";
import { useMockupStore } from "@/datastore/module/mockup.js";
import { Icon } from "@iconify/vue";
import PhotoshootModal from "@/components/modals/PhotoshootModal.vue";
import VideoGenerationModal from "@/components/modals/VideoGenerationModal.vue";
import { Skeleton } from "@/components/ui/skeleton";
import GenerationCompleteSonner from "@/views/mockup/editor/components/GenerationCompleteSonner.vue";
import { toast } from "vue-sonner";
import AdvanceImage from "@/components/common/AdvanceImage.vue";
import { debounce } from "es-toolkit";

const isSidebarOpen = ref(false);
const aiPhotoshootData = ref();
const aiVideoData = ref();
const selectedIndex = ref(0);
const selectedVideoModalData = ref();
const isSkeletonLoading = ref(false);
const runningTask = ref(0);
let unsubscribe = () => {};

const store = useMockupStore();

const showGenToast = debounce(
  (data) => {
    toast.custom(markRaw(GenerationCompleteSonner), {
      duration: Infinity,
      componentProps: {
        hideCloseBtn: true,
        title: "Ready",
        description: "Click to view",
        thumbnail: data?.thumbnail,
      },
      style: {
        borderRadius: "10px",
        right: "0px",
        top: "50px",
        height: "max-content",
      },
    });
  },
  1000,
  { leading: true, trailing: false },
);

useSubscription(
  invokeDownloadStatusAPI.subscribe(async () => {
    try {
      unsubscribe = await FirebaseServices.getVideoMockupStatus(
        authUser.value?.email,
        async (data) => {
          if (!data || !data?.length) {
            return;
          }
          if (data?.length === 1) {
            openGenerationSidebar.next();
          }
          const currentTask = data[0];
          if (currentTask.status == "Success" && currentTask?.downloadUrl) {
            if (runningTask.value != 0) {
              runningTask.value -= 1;
            }
            store.updateAIAnimateLoader(false);
            showGenToast(currentTask);
          } else if (currentTask.status == "Fail") {
            if (runningTask.value != 0) {
              runningTask.value -= 1;
            }
            store.updateAIAnimateLoader(false);
          } else {
            runningTask.value += 1;
          }
          aiVideoData.value = data;
        },
      );
    } catch (e) {
      store.updateAIAnimateLoader(false);
      console.error(e);
    }
  }),
);

useSubscription(
  openGenerationSidebar.subscribe(() => {
    isSidebarOpen.value = true;
  }),
);

useSubscription(
  closeGenerationSidebar.subscribe(() => {
    isSidebarOpen.value = false;
  }),
);

useSubscription(
  startSkeletonLoader.subscribe(() => {
    isSkeletonLoading.value = true;
  }),
);

useSubscription(
  stopSkeletonLoader.subscribe(() => {
    isSkeletonLoading.value = false;
  }),
);

const authUser = computed(() => store.authUser);
const runningTaskLength = computed(() => runningTask.value || 0);
const SidebarPanelData = computed(() => {
  if (aiPhotoshootData.value?.length || aiVideoData.value?.length) {
    const combinedData = [
      ...(Array.isArray(aiPhotoshootData.value)
        ? aiPhotoshootData.value.map((item) => ({
            ...item,
            type: "aiPhotoshoot",
          }))
        : []),

      ...(Array.isArray(aiVideoData.value)
        ? aiVideoData.value.map((item) => ({
            ...item,
            type: "aiVideo",
          }))
        : []),
    ];
    return combinedData.sort((a, b) => b?.timestamp - a?.timestamp);
  }
  return null;
});

onMounted(async () => {
  if (authUser.value?.email) {
    await FirebaseServices.getBgGeneratedImg(authUser.value?.email, (data) => {
      aiPhotoshootData.value = data;
    });
    await downloadStatusAPI();
  }
});

onUnmounted(() => {
  unsubscribe();
});

const downloadStatusAPI = async () => {
  const data = await FirebaseServices.getVideoMockups(authUser.value?.email);
  if (!data || !data?.length) {
    return;
  }
  aiVideoData.value = data;
};

const openSidebar = async () => {
  openGenerationSidebar.next();
};

const handleThumbClick = (data) => {
  if (data.type == "aiPhotoshoot") {
    selectedIndex.value = aiPhotoshootData.value?.findIndex(
      (item) => item.timestamp == data.timestamp,
    );
    openPhotoshootModal.next();
  } else {
    selectedVideoModalData.value = data;
    openAIVideoModal.next();
  }
};

watch(
  () => SidebarPanelData.value,
  (newData) => {
    if (newData && newData?.length) {
      runningTask.value = newData.filter((item) => {
        return (
          item.type != "aiPhotoshoot" &&
          item.status != "Success" &&
          item.status != "Fail"
        );
      })?.length;
    }
  },
);
</script>
<style scoped>
.shadow-genaration-panel {
  box-shadow: -5px 5px 24px -5px #03071233;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.1s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
